import SectionContainer from "../../components/UI/SectionContainer";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {openAlert, openConfirm, exportExcel } from "../../utils/common";
import { getGlucoState } from "../../utils/formatter";
import Spinner from "../../components/UI/Spinner";
import Modal from "../../components/Layout/Modal";
import GlucoSearchBox from "../../components/UI/GlucoSearchBox";
import glucoService from "../../services/glucoService";
import { sanitizeInput, validateNumericInput } from "../../utils/xssProtection";

const UserGlucoData = () => {
    const { userIdx } = useParams();
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [glucoData, setGlucoData] = useState({
        idx: "",
        gluco: "0",
        glucoDate: "",
        glucoTime: "",
        isEdit: false,
    });

    const [validationErrors, setValidationErrors] = useState({});

    const [glucoList, setGlucoList] = useState([]);

    const [glucoState, setGlucoState] = useState("0");
    const [year, setYear] = useState("0");
    const [month, setMonth] = useState("0");
    const [selectedIds, setSelectedIds] = useState([]);

    //체크박스 전체 선택
    const handleCheckAll = (e) => {
        if (e.target.checked) {
          // 모든 아이디를 한꺼번에 추가
          setSelectedIds(glucoList.map((item) => item.idx));
        } else {
          setSelectedIds([]);
        }
    };

    //체크박스 개별 선택
    const handleCheck = (idx, checked) => {
        setSelectedIds(prev =>
            checked
              ? [...prev, idx]
              : prev.filter(itemId => itemId !== idx)
        );
    };

    const fetchUserGlucoList = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                state: glucoState && glucoState !== "0" ? glucoState : null,
                year: year && year !== "0" ? year : null,
                month: month && month !== "0" ? month : null
            };
            
            const response = await glucoService.getUserGlucoList(userIdx, params);

            console.log(response);
            
            if (response.success) {
                setGlucoList(response.data.glucoList || []);
            } else {
                openAlert({title: "알림", description: response.message || "혈당수치 내역을 가져오는 중 오류가 발생했습니다."});
            }
        } catch (e) {
            const errorMessage = e.response?.data?.message || e.message || '혈당수치 내역을 가져오는 중 오류가 발생했습니다.';
            openAlert({title: "알림", description: errorMessage});
        } finally {
            setLoading(false);
        }
    }, [userIdx, glucoState, year, month]);

    // 필터 조건 변경 시 즉시 데이터 다시 가져오기
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const params = {
                    state: glucoState && glucoState !== "0" ? glucoState : null,
                    year: year && year !== "0" ? year : null,
                    month: month && month !== "0" ? month : null
                };
                
                const response = await glucoService.getUserGlucoList(userIdx, params);
                
                if (response.success) {
                    const newGlucoList = response.data.glucoList || [];
                    setGlucoList(newGlucoList);
                } else {
                    openAlert({title: "알림", description: response.message || "혈당수치 내역을 가져오는 중 오류가 발생했습니다."});
                }
            } catch (e) {
                const errorMessage = e.response?.data?.message || e.message || '혈당수치 내역을 가져오는 중 오류가 발생했습니다.';
                openAlert({title: "알림", description: errorMessage});
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, [userIdx, glucoState, year, month]);

    const getCurrentDateTime = () => {
        let currentDate = new Date();
        let currentYear = currentDate.getFullYear();
        let currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
        let currentDay = String(currentDate.getDate()).padStart(2, '0');
        let currentHours = String(currentDate.getHours()).padStart(2, '0');
        let currentMinutes = String(currentDate.getMinutes()).padStart(2, '0');
        return {
            glucoDate: `${currentYear}-${currentMonth}-${currentDay}`,
            glucoTime: `${currentHours}:${currentMinutes}`,
        }
    }

    const openEditGlucoModal = (item) => {
        setGlucoData({
            idx: item.idx,
            gluco: item.gluco,
            glucoDate: item.regDate.split(" ")[0],
            glucoTime: item.regDate.split(" ")[1],
            isEdit: true,
        });
        setModalOpen(true);
    }

    const onEditGluco = async () => {
        // 입력값 검증
        if (!glucoData.gluco || validationErrors.gluco) {
            openAlert({title: "알림", description: "올바른 혈당값을 입력해주세요. (20-600 mg/dL)"});
            return;
        }

        let currentDate = getCurrentDateTime();

        let glucoDate = glucoData.glucoDate;
        if(glucoDate === ""){
            glucoDate = currentDate.glucoDate;
        }

        let glucoTime = glucoData.glucoTime;
        if(glucoTime === ""){
            glucoTime = currentDate.glucoTime;
        }
        setLoading(true);
        try {
            const updateData = {
                userIdx: userIdx,
                glucoseLevel: glucoData.gluco,
                measurementDate: `${glucoDate} ${glucoTime}`,
                glucoIdx: glucoData.idx
            };
            
            const response = await glucoService.updateGluco(glucoData.idx, updateData);
            
            if (response.success) {
                closeRegisterGlucoModal();
                openAlert({title: "알림", description: "혈당값이 수정되었습니다.", response_method: "fetchUserGlucoList"});
            } else {
                openAlert({title: "알림", description: response.message || "혈당값 수정 중 오류가 발생했습니다."});
            }
        }catch(e){
            console.log(e);
            openAlert({title:"알림", description: "혈당값 수정 중 오류가 발생했습니다."});
        }finally{
            setLoading(false);
        }
    }

    const onRegistGluco = async () => {
        // 입력값 검증
        if (!glucoData.gluco || validationErrors.gluco) {
            openAlert({title: "알림", description: "올바른 혈당값을 입력해주세요. (20-600 mg/dL)"});
            return;
        }

        setLoading(true);
        try {
            const createData = {
                userIdx: userIdx,
                glucoseLevel: glucoData.gluco,
                measurementDate: `${glucoData.glucoDate} ${glucoData.glucoTime}`
            };
            
            const response = await glucoService.createGluco(createData);
            
            if (response.success) {
                closeRegisterGlucoModal();
                openAlert({title: "알림", description: "혈당값이 등록되었습니다.", response_method: "fetchUserGlucoList"});
            } else {
                openAlert({title: "알림", description: response.message || "혈당값 등록 중 오류가 발생했습니다."});
            }
        }catch(e){
            console.log(e);
            openAlert({title:"알림", description: "혈당값 등록 중 오류가 발생했습니다."});
        }finally{
            setLoading(false);
        }
    }

    const openRegisterGlucoModal = () => {
        let currentDate = getCurrentDateTime();
        setGlucoData({
            idx: "",
            gluco: "",
            glucoDate: currentDate.glucoDate,
            glucoTime: currentDate.glucoTime,
            isEdit: false,
        });
        setModalOpen(true);
    }

    const closeRegisterGlucoModal = () => {
        setGlucoData({
            idx: "",
            gluco: "",
            glucoDate: "",
            glucoTime: "",
            isEdit: false,
        });
        setModalOpen(false);
    }

    const onDeleteGluco = useCallback(async () => {
        setLoading(true);
        try {
            // 일괄 삭제 API 호출
            const response = await glucoService.deleteGlucoList(selectedIds);
            
            if (response.success) {
                setSelectedIds([]);
                openAlert({
                    title: "알림", 
                    description: `${response.data.deletedCount}건의 혈당값이 삭제되었습니다.`, 
                    response_method: "fetchUserGlucoList"
                });
            } else {
                openAlert({title: "알림", description: response.message || "혈당값 삭제 중 오류가 발생했습니다."});
            }
        } catch(e) {
            console.log(e);
            openAlert({title: "알림", description: "혈당값 삭제 중 오류가 발생했습니다."});
        } finally {
            setLoading(false);
        }
    }, [selectedIds]);

    const onDeleteGlucoConfirm = () => {    
        if(selectedIds.length === 0){
            openAlert({title:"알림", description: "삭제할 혈당값을 선택해주세요."});
            return;
        }
        openConfirm({title:"삭제", description: "삭제하시겠습니까?", positive_method: "onDeleteGluco", positive_btn: "삭제", negative_btn: "취소"});
    }

    const onGlucoExcelDownload = async () => {
        try {
            const params = {
                state: glucoState && glucoState !== "0" ? glucoState : null,
                year: year && year !== "0" ? year : null,
                month: month && month !== "0" ? month : null
            };
            
            const response = await glucoService.downloadUserGlucoExcel(userIdx, params);
            
            if (response.success) {
                exportExcel(response.data, {
                    excelFileName: "회원 혈당수치 데이터.xlsx",
                    sheetName: "Sheet1"
                });
            } else {
                openAlert({title: "알림", description: response.message || "엑셀 다운로드 중 오류가 발생했습니다."});
            }
        }catch(e){
            console.log(e);
            openAlert({title:"알림", description: "혈당수치 내역을 가져오는 중 오류가 발생했습니다."});
        }
    }
    
    const numberCheck = (e) => {
        const rawValue = e.target.value;
        const sanitizedValue = sanitizeInput(rawValue);
        const numericValue = sanitizedValue.replace(/[^0-9]/g, "");
        
        // 혈당값 범위 검증 (일반적으로 20-600 mg/dL)
        const validation = validateNumericInput(numericValue, 20, 600);
        
        setValidationErrors(prev => ({
            ...prev,
            gluco: validation.isValid ? null : validation.error
        }));
        
        setGlucoData({
            ...glucoData,
            gluco: numericValue,
        });
    }
    const handleSetYear = (year) => {
        setYear(year);
        setSelectedIds([]); // 필터 변경 시 선택된 아이템 초기화
    }
    const handleSetMonth = (month) => {
        setMonth(month);
        setSelectedIds([]); // 필터 변경 시 선택된 아이템 초기화
    }
    const handleSetGlucoState = (glucoState) => {
        setGlucoState(glucoState);
        setSelectedIds([]); // 필터 변경 시 선택된 아이템 초기화
    }

    useEffect(() => {
        window.fetchUserGlucoList = fetchUserGlucoList;
        window.onDeleteGluco = onDeleteGluco;
        return () => {
            delete window.fetchUserGlucoList;
            delete window.onDeleteGluco;
        }
    }, [fetchUserGlucoList, onDeleteGluco]);
    
    return (
        <SectionContainer title="회원 혈당수치 조회">
            <Spinner loading={loading} />
            <GlucoSearchBox 
                onAddGluco={openRegisterGlucoModal}
                onDelGluco={onDeleteGlucoConfirm}
                onExcelDownload={onGlucoExcelDownload}
                setYear={handleSetYear}
                setMonth={handleSetMonth}
                setGlucoState={handleSetGlucoState}
            />
            <div className="account_content">
                <div className="content_area con01">
                    <div className="table_area popup_table_area">
                        <div className="table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>                                                
                                            <div className="input">
                                                <input type="checkbox" 
                                                    checked={selectedIds.length === glucoList.length && glucoList.length>0}
                                                    onChange={handleCheckAll}
                                                />
                                            </div>
                                        </th>
                                        <th>측정결과</th>
                                        <th>측정상태</th>
                                        <th>측정날짜</th>
                                        <th>측정시간</th>
                                    </tr>
                                </thead>
                                <tbody id="wrap" data-wrap="wrap">
                                    {!loading && glucoList.length === 0 ? (
                                        <tr><td colSpan="5">혈당수치 내역이 존재하지 않습니다.</td></tr>
                                    ) : (
                                        glucoList.map((item, index) => {
                                            return (
                                                <tr key={index} onClick={() => openEditGlucoModal(item)}>
                                                    <td>
                                                        <div className="input">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selectedIds.includes(item.idx)}
                                                                onChange={(e) => {
                                                                    handleCheck(item.idx, e.target.checked);
                                                                    e.stopPropagation();
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>{item.gluco}mg/dl</td>
                                                    <td><span style={{ color: getGlucoState(item.gluco).color }}>{getGlucoState(item.gluco).text}</span></td>
                                                    <td>{item.regDate? item.regDate.split(" ")[0] : ""}</td>
                                                    <td>{item.regDate? item.regDate.split(" ")[1] : ""}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                title="혈당값 관리"
                className="terminal-info mt-2"
                modalClassName=" modal-ml"
                onConfirm={glucoData.isEdit ? onEditGluco : onRegistGluco}
                confirmLabel={glucoData.isEdit ? "수정" : "등록"}
                onClose={closeRegisterGlucoModal}
                visible={modalOpen}
            >
                <dl>
                    <dt>혈당값</dt>
                    <dd>
                        <input 
                            type="text" 
                            onChange={numberCheck} 
                            value={glucoData.gluco}
                            style={{
                                border: validationErrors.gluco ? '2px solid #ff4444' : '1px solid #ddd'
                            }}
                            placeholder="혈당값 입력 (20-600 mg/dL)"
                        />
                        {validationErrors.gluco && (
                            <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '5px' }}>
                                {validationErrors.gluco}
                            </div>
                        )}
                    </dd>
                </dl>

                <dl>
                    <dt>측정일</dt>
                    <dd>
                        <input type="date" onChange={(e) => setGlucoData({ ...glucoData, glucoDate: e.target.value })} value={glucoData.glucoDate}/>
                    </dd>
                </dl>

                <dl>
                    <dt>측정시간</dt>
                    <dd>
                        <input type="time" onChange={(e) => setGlucoData({ ...glucoData, glucoTime: e.target.value })} value={glucoData.glucoTime}/>
                    </dd>
                </dl>

                <p style={{marginTop: "10px", color: "red", textAlign: "center"}}>※ 측정일, 측정시간 미입력시 현재 시간으로 저장됩니다.</p>

            </Modal>
            
        </SectionContainer>
    );
}

export default UserGlucoData;
